/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import assign from 'lodash/assign';

import { useAppNavigate } from 'hooks';
import { IDynamicAction, IComponentParams } from 'plugins/api/types';
import { useCallCommand, useAdd, useUpdate } from 'plugins/api';
import {
  loadDataUrlParser,
  paramsToUrlParser,
  paramsListToRequestParser
} from 'plugins/util/parser';
import * as Components from 'components/containers';
import { DynamicPage } from 'plugins/pages';

import { Button } from '@mui/material';
import { Modal } from 'components/controls';

// typ danych wejściowych
interface IProps {
  action: IDynamicAction;
  handleClose: () => void;
  refetchEditPanelData: () => void;
  componentParams?: IComponentParams;
  requestParams?: any;
  requestState?: any;
  pageSymbol: string;
}

type IComponentKeys = 'ClientForm' | 'Order';

const Action: FC<IProps> = ({
  action,
  handleClose,
  refetchEditPanelData,
  requestParams,
  requestState,
  componentParams,
  pageSymbol
}) => {
  const { search, pathname } = useLocation();
  const navigate = useAppNavigate();

  const [open, setOpen] = useState(false);

  // import komponentu po nazwie w definicji akcji
  const componentKey = action?.method_params?.component_name;
  const Component =
    typeof componentKey === 'string' ? Components[componentKey as IComponentKeys] : null;

  const getPosition = (string: string, subString: string, index: number) => {
    return string.split(subString, index).join(subString).length;
  };

  const urlPrefix = pathname.slice(0, getPosition(pathname, '/', 2));

  // endpoint url dla komendy Add
  const addUrl = loadDataUrlParser(action.method_params?.command_url || '', {
    ...requestParams,
    ...qs.parse(search)
  });

  const { mutate: callCommand } = useCallCommand(addUrl || '', {
    onSuccess: () => {
      if (action.refresh_data_mode === 'ONSUCCESS') {
        refetchEditPanelData();
        componentParams?.refetchFn?.();
        handleClose();
      }
    }
  });

  const { mutate: add } = useAdd(addUrl || '', {
    onSuccess: (data) => {
      if (action?.method_params?.result_params_to_save) {
        const paramsUrl = paramsToUrlParser(action.method_params?.result_params_to_save, {
          ...qs.parse(window.location.search),
          ...data.data
        });

        paramsUrl && navigate(pathname.replace(`${urlPrefix}`, '') + `?${paramsUrl}`);
      }

      if (action.refresh_data_mode === 'ONSUCCESS') {
        refetchEditPanelData();
        componentParams?.fnCallback?.();
        componentParams?.refetchFn?.();
      }
    }
  });

  const { mutate: update } = useUpdate(addUrl || '', {
    onSuccess: () => {
      if (action.refresh_data_mode === 'ONSUCCESS') {
        refetchEditPanelData();
        componentParams?.fnCallback?.();
        componentParams?.refetchFn?.();
      }
    }
  });

  const handleAction = () => {
    if (action.method === 'OpenModalComponent' || action.method === 'OpenModalDynamicPage') {
      setOpen(true);
    }

    if (action.method === 'Cancel') {
      handleClose();
    }

    if (action.method === 'Redirect' && action.method_params?.page_url) {
      navigate(action.method_params?.page_url);
    }

    const requestStateData = assign({}, ...Object.values(requestState[pageSymbol]));
    const prepareData = {
      ...(typeof requestStateData === 'object' ? requestStateData : {}),
      ...(requestParams || {}),
      ...qs.parse(search)
    };

    if (action.method === 'CallCommand') {
      const request = paramsListToRequestParser(action.method_params?.params_list || '', {
        prepareData
      });

      callCommand(request);
    }

    if (action.method === 'Add') {
      const request = paramsListToRequestParser(action.method_params?.params_list || '', {
        prepareData
      });

      add(request);
    }

    if (action.method === 'Update') {
      const request = paramsListToRequestParser(action.method_params?.params_list || '', {
        prepareData
      });

      update(request);
    }
  };

  const handleSuccess = () => {
    setOpen(false);

    if (action.refresh_data_mode === 'ONSUCCESS') {
      refetchEditPanelData();
    }
  };

  const renderModalContent = () => {
    if (action.method === 'OpenModalComponent') {
      return (
        <Modal onClose={() => setOpen(false)} title="">
          {Component && (
            <Component onCancel={() => setOpen(false)} onSuccess={() => handleSuccess()} />
          )}
        </Modal>
      );
    }

    if (action.method === 'OpenModalDynamicPage') {
      const requestParams = action.method_params?.params_list
        ? qs.parse(paramsToUrlParser(action.method_params?.params_list, { ...qs.parse(search) }))
        : {};

      return (
        <Modal fullWidth onClose={() => setOpen(false)} title="">
          <DynamicPage
            pageSymbol={action.method_params?.dynamic_page || ''}
            componentParams={{
              fnCallback: () => setOpen(false),
              isModal: false,
              refetchFn: refetchEditPanelData
            }}
            requestParams={requestParams}
          />
        </Modal>
      );
    }

    return null;
  };

  return (
    <>
      <Button variant="contained" color="secondary" onClick={handleAction}>{action.label}</Button>
      {open && renderModalContent()}
    </>
  );
};

export default Action;
